'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { AlertTriangle, RotateCw, Home } from 'lucide-react'

/**
 * Top-level error boundary for the app router. Next.js 14 expects this
 * file to exist at the route root — without it, the runtime emits the
 * "missing required error components" warning and tries to recover by
 * refreshing the page in a loop.
 *
 * Per Next.js docs, error.tsx MUST be a Client Component and accepts a
 * `reset` callback that re-renders the segment.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Surface to the console in dev so you can see the actual stack
    console.error('[App Error Boundary]', error)
  }, [error])

  return (
    <div className="min-h-screen flex items-center justify-center p-6 liquid-glass-bg">
      <div className="max-w-md w-full glass-card p-8 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-500/10 mb-5">
          <AlertTriangle className="w-7 h-7 text-red-500 dark:text-red-400" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-[#f0f0f5] mb-2">
          Something went wrong
        </h1>
        <p className="text-sm text-slate-600 dark:text-[#8a8d9b] leading-relaxed mb-6">
          {error.message || 'An unexpected error occurred while rendering this page.'}
        </p>
        {error.digest && (
          <p className="text-[10px] font-mono text-slate-400 dark:text-[#555766] mb-5">
            ref: {error.digest}
          </p>
        )}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl hover:from-blue-500 hover:to-blue-400 shadow-md shadow-blue-500/20 transition-all"
          >
            <RotateCw className="w-3.5 h-3.5" />
            Try again
          </button>
          <Link
            href="/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold text-slate-600 bg-white/50 border border-white/40 rounded-xl hover:bg-white/70 dark:text-[#8a8d9b] dark:bg-white/[0.04] dark:border-white/[0.06] dark:hover:bg-white/[0.08] transition-all"
          >
            <Home className="w-3.5 h-3.5" />
            Dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}
